/**
 * API Client for Weiwei Car Platform
 * Handles all HTTP requests to the backend API
 */

const API_BASE_URL = (import.meta.env as { VITE_API_URL?: string }).VITE_API_URL || 'http://localhost:8787';

export type ApiResponse<T> = {
  success: boolean;
  data?: T;
  pagination?: PaginationInfo;
  facets?: import('../types').SearchFacetsData;
  error?: {
    code: string;
    message: string;
  };
};

export type PaginationParams = {
  limit?: number;
  offset?: number;
};

export type PaginationInfo = {
  total: number;
  limit: number;
  offset: number;
};

export type PaginatedResponse<T> = {
  data: T[];
  pagination: PaginationInfo;
};

/**
 * Generic API request handler
 */
export async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  const token = localStorage.getItem('auth_token');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Handle 204 No Content responses (e.g., DELETE requests)
    if (response.status === 204) {
      return {
        success: true,
        data: undefined,
      };
    }

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          code: data.error?.code || 'UNKNOWN_ERROR',
          message: data.error?.message || 'An error occurred',
        },
      };
    }

    // Backend returns: { success: true, data: [...], pagination: {...}, facets: {...} }
    // Return the response structure directly
    return {
      success: true,
      data: data.data,
      pagination: data.pagination,
      facets: data.facets,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network error occurred',
      },
    };
  }
}

/**
 * HTTP Methods
 */
// Helper function to get full backend response (useful for endpoints with extra properties like 'settings')
async function requestFull<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  const token = localStorage.getItem('auth_token');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Handle 204 No Content responses (e.g., DELETE requests)
    if (response.status === 204) {
      return {
        success: true,
        data: undefined,
      };
    }

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          code: data.error?.code || 'UNKNOWN_ERROR',
          message: data.error?.message || 'An error occurred',
        },
      };
    }

    // Return the full backend response (not just data.data)
    return {
      success: true,
      data: data,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network error occurred',
      },
    };
  }
}

export const api = {
  get: <T>(endpoint: string, params?: Record<string, string | number | boolean | undefined | null>) => {
    const queryString = params
      ? '?' + new URLSearchParams(
          Object.fromEntries(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
              .map(([k, v]) => [k, String(v)])
          )
        ).toString()
      : '';
    return request<T>(`${endpoint}${queryString}`, {
      method: 'GET',
    });
  },

  // Get full backend response (useful for endpoints with extra properties like 'settings')
  getFull: <T>(endpoint: string, params?: Record<string, string | number | boolean | undefined | null>) => {
    const queryString = params
      ? '?' + new URLSearchParams(
          Object.fromEntries(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
              .map(([k, v]) => [k, String(v)])
          )
        ).toString()
      : '';
    return requestFull<T>(`${endpoint}${queryString}`, {
      method: 'GET',
    });
  },

  post: <T>(endpoint: string, body: unknown) => {
    return request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  put: <T>(endpoint: string, body: unknown) => {
    return request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  patch: <T>(endpoint: string, body: unknown) => {
    return request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  delete: <T>(endpoint: string) => {
    return request<T>(endpoint, {
      method: 'DELETE',
    });
  },
};

export default api;
